/**
 * "MEU E-MAIL CHEGOU?" — o diagnóstico do que SAI, para quem usa a caixa.
 *
 * A pergunta que esta tela existe para responder é a mais comum do suporte de
 * qualquer plataforma de e-mail, e a resposta mora no servidor desde a fase 3
 * (`cdw.fila_eventos`, alimentada pelo coletor do log do Postfix). O que
 * faltava era alguém poder perguntar sem ser administrador.
 *
 * ESTAR EM "ENVIADOS" NÃO QUER DIZER QUE CHEGOU, e é isso que quase ninguém
 * sabe: aquela pasta diz que a mensagem SAIU daqui. O que aconteceu depois —
 * aceita, adiada, recusada — é outra coisa, e é esta tela.
 *
 * O RESUMO VEM ANTES DA LISTA. Quem abre isto quer uma resposta, não um log:
 * três números dizem em um olhar se há algo errado, e a lista existe para
 * quem precisa do detalhe.
 *
 * FRONTEIRA ENTRE CLIENTES (regra 5): quem isola é `escopoDeEventos` na API,
 * que para um usuário comum já limita os eventos ao e-mail dele. O filtro
 * `de` daqui escolhe o LADO (o que saiu), nunca o dono.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { entregas } from "../api/admin";
import { useCaixa } from "../sessao/contexto";
import { Icone } from "../ui/Icone";
import { dataCurta, dataLonga } from "../ui/formato";
import { DIAS_PADRAO, desdeQuando } from "./periodo";

const PERIODOS = [
  { rotulo: "Últimos 7 dias", dias: 7 },
  { rotulo: "Últimos 30 dias", dias: 30 },
];

/**
 * COMO CADA EVENTO APARECE.
 *
 * O rótulo é o VEREDITO em duas ou três palavras — é o que a pessoa lê
 * primeiro e muitas vezes o único que ela precisa. A explicação detalhada e o
 * que fazer vêm do servidor, já em português: repetir aqui as mesmas frases
 * criaria duas versões para divergirem.
 */
function comoMostrar(tipo?: string): {
  rotulo: string;
  icone: "Check" | "AlarmClock" | "X";
  cor: string;
  fundo: string;
} {
  switch (tipo) {
    case "entregue":
      return { rotulo: "Chegou", icone: "Check", cor: "text-acao", fundo: "bg-acao-clara" };
    case "adiado":
      return {
        rotulo: "Ainda tentando",
        icone: "AlarmClock",
        cor: "text-aviso",
        fundo: "bg-aviso/20",
      };
    case "quarentena":
      return {
        rotulo: "Separado como spam",
        icone: "AlarmClock",
        cor: "text-aviso",
        fundo: "bg-aviso/20",
      };
    default:
      // rejeitado, bounce, e o que vier depois. Errar para o lado de "não
      // chegou" é o certo: é o estado que pede ação.
      return { rotulo: "Não chegou", icone: "X", cor: "text-perigo", fundo: "bg-perigo/15" };
  }
}

/** Um envio: a mensagem, e tudo que aconteceu com ela. */
export type Envio = {
  chave: string;
  destinatario: string;
  tipo: string;
  explicacao?: string | null;
  acao?: string | null;
  ocorrido_em: string;
  tentativas: number;
  desde: string;
  /**
   * O assunto da mensagem, quando o servidor o manda.
   *
   * Vazio é estado normal e tem DUAS causas que a tela trata igual: evento
   * anterior à captura do Message-ID (31/10/2026), ou quem pergunta não é o
   * dono da mensagem. Ver interno/web/entregas.go.
   */
  assunto?: string;
};

/**
 * Agrupa os EVENTOS por mensagem.
 *
 * A tela listava `cdw.fila_eventos` direto, e cada tentativa de entrega é um
 * evento. Medido em 30/10/2026: a fila `D354128B868` para
 * `victorshi@parisi.com` tem 103 eventos "adiado" — UMA mensagem que virou
 * 103 cartões iguais na tela, e um contador dizendo "103 ainda tentando".
 *
 * O número ficava absurdo justamente quando a pessoa mais precisa dele: um
 * destino que não responde gera dezenas de tentativas, e a tela respondia
 * "você enviou 103 mensagens" para quem enviou uma.
 *
 * A CHAVE É O `queue_id`: é o identificador que o Postfix dá à mensagem, e
 * ele é o mesmo em todas as tentativas. Sem ele — rejeição na conversa SMTP,
 * que nunca chegou a entrar na fila — cada evento é um envio só, e o `uuid`
 * serve de chave.
 *
 * O ESTADO QUE VALE É O ÚLTIMO. Uma mensagem adiada cinco vezes e entregue na
 * sexta CHEGOU; mostrá-la como "ainda tentando" porque houve adiamento seria
 * responder a pergunta errada.
 */
export function agruparPorMensagem(
  eventos: {
    uuid?: string | null;
    queue_id?: string | null;
    destinatario?: string | null;
    tipo?: string | null;
    explicacao?: string | null;
    acao?: string | null;
    ocorrido_em?: string | null;
    assunto?: string | null;
  }[],
): Envio[] {
  const porChave = new Map<string, Envio>();
  for (const e of eventos) {
    const destinatario = e.destinatario ?? "";
    const quando = e.ocorrido_em ?? "";
    // Destinatário entra na chave: uma mensagem para três pessoas é uma
    // entrega por pessoa, e podem terminar diferente.
    const chave = e.queue_id
      ? `${e.queue_id}|${destinatario}`
      : (e.uuid ?? `${destinatario}|${quando}`);

    const atual = porChave.get(chave);
    if (!atual) {
      porChave.set(chave, {
        chave,
        destinatario,
        tipo: e.tipo ?? "",
        explicacao: e.explicacao,
        acao: e.acao,
        ocorrido_em: quando,
        tentativas: 1,
        desde: quando,
        assunto: e.assunto ?? "",
      });
      continue;
    }
    atual.tentativas += 1;
    if (quando < atual.desde) atual.desde = quando;
    // O ASSUNTO NÃO SEGUE O ESTADO. Ele é o mesmo em todas as tentativas, e
    // num evento pode vir vazio por não ter Message-ID guardado — então vale
    // o primeiro que aparecer, e não o do evento mais recente. Pondo-o junto
    // do `tipo` lá embaixo, uma tentativa sem assunto apagaria o que já
    // estava no cartão.
    if (!atual.assunto && e.assunto) atual.assunto = e.assunto;
    // O mais recente manda no estado mostrado.
    if (quando >= atual.ocorrido_em) {
      atual.ocorrido_em = quando;
      atual.tipo = e.tipo ?? atual.tipo;
      atual.explicacao = e.explicacao;
      atual.acao = e.acao;
    }
  }
  return [...porChave.values()].sort((a, b) =>
    a.ocorrido_em < b.ocorrido_em ? 1 : a.ocorrido_em > b.ocorrido_em ? -1 : 0,
  );
}

export function MeuEmailChegou() {
  const eu = useCaixa();
  const [para, setPara] = useState("");
  const [dias, setDias] = useState(DIAS_PADRAO);
  const [busca, setBusca] = useState({ para: "", dias: DIAS_PADRAO });

  const consulta = useQuery({
    queryKey: ["meu-email-chegou", busca],
    queryFn: () =>
      entregas.listar({
        por_pagina: 50,
        // `de`, e não `endereco`: aqui só interessa o que SAIU desta caixa.
        de: eu.caixa,
        para: busca.para || undefined,
        desde: desdeQuando(busca.dias),
      }),
  });

  // MENSAGENS, não eventos: ver agruparPorMensagem.
  const itens = agruparPorMensagem(consulta.data?.itens ?? []);
  // O CONTADOR FILTRA. Ele respondia "4 chegaram, 45 tentando" e parava
  // ali: a pessoa lia o número e tinha que procurar as 45 no meio da lista
  // à mão. O número é a pergunta; a lista filtrada é a resposta.
  const [sofilho, setSoFilho] = useState<string | null>(null);
  const mostrados = sofilho
    ? itens.filter((e) =>
        sofilho === "entregue"
          ? e.tipo === "entregue"
          : sofilho === "adiado"
            ? e.tipo === "adiado"
            : e.tipo !== "entregue" && e.tipo !== "adiado",
      )
    : itens;
  const chegaram = itens.filter((e) => e.tipo === "entregue").length;
  const tentando = itens.filter((e) => e.tipo === "adiado").length;
  const falharam = itens.filter(
    (e) => e.tipo !== "entregue" && e.tipo !== "adiado",
  ).length;

  return (
    /* O QUE ROLA É O CONTEÚDO, NÃO A PÁGINA.
       A revisão de UX de 30/10/2026 apontou "layout não contido na altura da
       tela, em todas as telas". Estas duas eram as que de fato escapavam: o
       esqueleto do webmail tem altura fixa (`h-[100dvh]`) e espera que cada
       tela filha seja um item de flex que rola por dentro — é o que Lista,
       Leitura, Busca e Contatos fazem. Aqui a raiz era só um `mx-auto`, sem
       `flex-1` e sem rolagem: com muitos eventos listados, o conteúdo
       transbordava o esqueleto por baixo, fora de alcance, e nenhuma barra de
       rolagem aparecia para chegar nele.
       `min-w-0` porque a tabela de eventos é larga: sem ele, um destinatário
       comprido alarga este item de flex e empurra a barra lateral. */
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <h1 className="text-xl font-semibold text-tinta sm:text-2xl">
          Meu e-mail chegou?
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-tinta-media">
          O que aconteceu depois que você apertou enviar.{" "}
          <strong className="text-tinta">
            Estar em “Enviados” não quer dizer que chegou
          </strong>{" "}
          — quer dizer que saiu daqui. É esta tela que mostra o resto do caminho.
        </p>

        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            setBusca({ para: para.trim(), dias });
          }}
          className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-linha bg-white p-4"
        >
          <label className="min-w-[240px] flex-1 text-xs text-tinta-fraca">
            Para quem você enviou?
            <input
              value={para}
              onChange={(ev) => setPara(ev.target.value)}
              placeholder="em branco mostra tudo"
              className="mt-1 w-full rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
            />
          </label>
          <label className="text-xs text-tinta-fraca">
            Quando
            <select
              value={dias}
              onChange={(ev) => setDias(Number(ev.target.value))}
              className="mt-1 block rounded-lg border border-linha bg-white px-3 py-2 text-sm text-tinta"
            >
              {PERIODOS.map((p) => (
                <option key={p.dias} value={p.dias}>
                  {p.rotulo}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className="rounded-full bg-acao px-5 py-2.5 text-sm font-medium text-white"
          >
            Procurar
          </button>
        </form>

        {consulta.isLoading && (
          <p className="mt-6 text-sm text-tinta-fraca">Procurando…</p>
        )}

        {!consulta.isLoading && itens.length > 0 && (
          <>
            {/* O RESUMO RESPONDE ANTES DA LISTA. */}
            <div className="mt-5 grid grid-cols-3 gap-3">
              {[
                { n: chegaram, rotulo: "chegaram", cor: "text-acao", filtro: "entregue" },
                { n: tentando, rotulo: "ainda tentando", cor: "text-aviso", filtro: "adiado" },
                { n: falharam, rotulo: "não chegaram", cor: "text-perigo", filtro: "falhou" },
              ].map((c) => (
                <button
                  key={c.rotulo}
                  type="button"
                  // Clicar de novo no mesmo cartão desfaz o filtro: sem isso a
                  // pessoa fica presa num recorte sem caminho de volta óbvio.
                  onClick={() => setSoFilho(sofilho === c.filtro ? null : c.filtro)}
                  aria-pressed={sofilho === c.filtro}
                  className={`rounded-xl border bg-white p-3 text-left ${
                    sofilho === c.filtro
                      ? "border-acao ring-1 ring-acao"
                      : "border-linha hover:border-acao"
                  }`}
                >
                  <p className={`text-2xl font-semibold leading-tight ${c.cor}`}>
                    {c.n}
                  </p>
                  <p className="mt-0.5 text-sm text-tinta-media">{c.rotulo}</p>
                </button>
              ))}
            </div>

            <div className="mt-4">
              {mostrados.map((e) => {
                const v = comoMostrar(e.tipo);
                return (
                  <article
                    key={e.chave}
                    className={`mt-2 flex gap-3 rounded-xl border border-linha border-l-[3px] bg-white p-4 ${
                      e.tipo === "entregue"
                        ? "border-l-acao"
                        : e.tipo === "adiado"
                          ? "border-l-aviso"
                          : "border-l-perigo"
                    }`}
                  >
                    <span
                      className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${v.fundo}`}
                    >
                      <Icone nome={v.icone} tamanho={13} className={v.cor} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <strong className="text-sm text-tinta">{v.rotulo}</strong>
                        <span className="text-sm text-tinta-media">
                          para {e.destinatario}
                        </span>
                        {e.tentativas > 1 && (
                          <span className="rounded-full bg-fundo-suave px-2 py-0.5 text-xs text-tinta-fraca">
                            {e.tentativas} tentativas
                          </span>
                        )}
                        {/* A HORA DO ÚLTIMO ACONTECIMENTO, dita como tal.
                            Com várias tentativas, "há 2 min" é quando a
                            última falhou — não quando a pessoa enviou. Os
                            dois são mostrados separados logo abaixo, porque
                            confundi-los faz a pessoa procurar na caixa de
                            Enviados um e-mail de outro horário. */}
                        <span
                          className="ml-auto text-xs text-tinta-fraca"
                          title={
                            e.tentativas > 1
                              ? "Quando foi a última tentativa"
                              : undefined
                          }
                        >
                          {dataCurta(e.ocorrido_em)}
                        </span>
                      </div>

                      {/* O ASSUNTO, que era o apontamento 05: o cartão dizia
                          só o destinatário, e três e-mails para a mesma
                          pessoa viravam três cartões indistinguíveis.
                          Vazio não desenha nada em vez de "(sem assunto)":
                          a maior parte dos eventos antigos não tem
                          Message-ID guardado, e uma coluna de "(sem
                          assunto)" se lê como mensagens sem assunto. */}
                      {e.assunto && (
                        <p className="mt-0.5 truncate text-sm text-tinta" title={e.assunto}>
                          {e.assunto}
                        </p>
                      )}

                      {/* QUANDO SAIU, separado de quando a última tentativa
                          aconteceu. Só aparece quando há mais de uma, senão
                          seria o mesmo instante escrito duas vezes. */}
                      {e.tentativas > 1 && (
                        <p className="mt-0.5 text-xs text-tinta-fraca">
                          enviado em {dataLonga(e.desde)}
                        </p>
                      )}
                      <p className="mt-1 text-sm text-tinta-media">
                        {e.explicacao}
                      </p>
                      {e.acao && (
                        <p className="mt-2 border-l-2 border-linha pl-3 text-xs text-tinta-fraca">
                          <strong className="text-tinta-media">
                            O que fazer:
                          </strong>{" "}
                          {e.acao}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {/* Mesmo princípio da outra tela: vazio não pode ser uma tela em branco.
            Aqui o vazio tem um segundo significado possível — a pessoa pode
            simplesmente não ter enviado nada —, e a frase cobre os dois. */}
        {!consulta.isLoading && itens.length === 0 && (
          <section className="mt-6 rounded-xl border border-dashed border-linha bg-white p-4">
            <h2 className="text-sm font-semibold text-tinta">
              Nenhum envio encontrado
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-tinta-media">
              Não há registro de mensagem saindo de{" "}
              <strong className="text-tinta">{eu.caixa}</strong> nos últimos{" "}
              {busca.dias} dias
              {busca.para && (
                <>
                  {" "}
                  para <strong className="text-tinta">{busca.para}</strong>
                </>
              )}
              .
            </p>
            <p className="mt-2 max-w-2xl text-sm text-tinta-media">
              Se você enviou e não aparece aqui, confira em{" "}
              <strong className="text-tinta">Rascunhos</strong>: a mensagem pode
              ter ficado sem ser enviada.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}
